import { useEffect, useRef, useState } from "react";
import { Button, Text, AlertBanner } from "snap-design-system";
import { Loader2, CheckCircle2, ShieldAlert, AlertCircle } from "lucide-react";
import { useLegacyMigrationWs } from "~/hooks/useLegacyMigrationWs";
import { useUserData } from "~/store/userStore";

/**
 * Ref compartilhado entre Content e Footer para comunicar estado
 * sem depender de re-renders do dialog (que captura JSX como snapshot).
 */
export interface LegacyMigrationRef {
    isComplete: boolean;
    hasFailed: boolean;
}

interface LegacyMigrationContentProps {
    totalItems: number;
    migrationRef: React.RefObject<LegacyMigrationRef>;
}

/**
 * Conteúdo do dialog de migração legada.
 * Usa WebSocket bidirecional para streaming de itens legados.
 * O WS é read-only para o DB — itens são acumulados em memória
 * e submetidos atomicamente via POST ao final.
 */
export function LegacyMigrationContent({
    totalItems,
    migrationRef,
}: LegacyMigrationContentProps) {
    const { start, progress, isComplete: wsComplete, isReady } = useLegacyMigrationWs();
    const userData = useUserData();
    const startedRef = useRef(false);

    // Atualiza ref compartilhado
    useEffect(() => {
        migrationRef.current = {
            isComplete: wsComplete,
            hasFailed: progress.failed > 0,
        };
    }, [wsComplete, progress.failed, migrationRef]);

    // Inicia migração via WS quando o hook estiver pronto.
    // Guard via useRef é síncrono — evita race condition do setState assíncrono.
    // `start` é estável (referência constante via useCallback([], [])), então
    // não causa re-execução do efeito.
    useEffect(() => {
        if (!startedRef.current && totalItems > 0 && isReady && userData?.user_id) {
            startedRef.current = true;
            start(userData.user_id);
        }
    }, [totalItems, isReady, userData?.user_id, start]);

    const { total, processed, failed, isRunning } = progress;
    const percentage = total > 0 ? Math.round(((processed + failed) / total) * 100) : 0;
    const hasFailures = failed > 0;

    return (
        <div className="space-y-4">
            <AlertBanner
                variant="information"
                icon={<ShieldAlert className="size-5" />}
                title="Atualização de segurança necessária"
                message="A criptografia organizacional foi ativada. Seus dados existentes precisam ser atualizados para o novo formato de segurança. Este processo é automático e não altera seus dados."
            />

            <div className="flex items-center gap-3">
                {isRunning ? (
                    <Loader2 className="w-5 h-5 animate-spin text-accent-500" />
                ) : wsComplete && !hasFailures ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : wsComplete && hasFailures ? (
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                ) : (
                    <Loader2 className="w-5 h-5 animate-spin text-accent-500" />
                )}
                <Text variant="body-md">
                    {isRunning
                        ? `Migrando dados (${processed}/${total})...`
                        : wsComplete && !hasFailures
                            ? "Migração concluída com sucesso!"
                            : wsComplete && hasFailures
                                ? `Migração finalizada com ${failed} erro(s).`
                                : "Preparando migração..."}
                </Text>
            </div>

            {total > 0 && (
                <>
                    <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-300 ${hasFailures ? "bg-amber-500" : "bg-accent-500"
                                }`}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    <div className="flex justify-between">
                        <Text variant="body-sm" color="subtle">
                            {processed} concluído(s){failed > 0 ? `, ${failed} falha(s)` : ""}
                        </Text>
                        <Text variant="body-sm" color="subtle">
                            {percentage}%
                        </Text>
                    </div>
                </>
            )}

            {isRunning && (
                <Text variant="body-sm" color="subtle">
                    Não feche esta janela. O processo pode levar alguns minutos
                    dependendo da quantidade de dados.
                </Text>
            )}
        </div>
    );
}

interface LegacyMigrationFooterProps {
    migrationRef: React.RefObject<LegacyMigrationRef>;
    onSuccess?: () => void;
}

export function LegacyMigrationFooter({
    migrationRef,
    onSuccess,
}: LegacyMigrationFooterProps) {
    const [canContinue, setCanContinue] = useState(false);

    // Poll o ref para detectar conclusão (necessário porque o dialog captura JSX como snapshot)
    useEffect(() => {
        const interval = setInterval(() => {
            if (migrationRef.current.isComplete) {
                setCanContinue(true);
                clearInterval(interval);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [migrationRef]);

    if (!canContinue) return null;

    return (
        <Button onClick={onSuccess}>
            Continuar
        </Button>
    );
}

export const LegacyMigrationDialog = {
    Content: LegacyMigrationContent,
    Footer: LegacyMigrationFooter,
};
